import { useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Send, X } from 'lucide-react';
import type { JobMobileState } from './use-job-mobile';
import { parsePeople } from './mobile-words';

type Person = { email: string; name?: string };

/**
 * One box that adds people: each address becomes a chip on Enter, a comma or a paste (a pasted list
 * becomes many; `Name <address>` keeps the name), and one button invites them all. What was refused stays
 * in the box with why; what is not an address is named back, never dropped in silence.
 */
export function MobileAddPeople({ state }: { state: JobMobileState }): ReactNode {
  const box = useChips(state);
  const count = box.chips.length + box.draftPeople.length;
  return (
    <div className="paige-mobile-add">
      <div className="paige-mobile-chips" onClick={(event) => focusInput(event.currentTarget)}>
        {box.chips.map((person) => (
          <Chip key={person.email} person={person} onDrop={() => box.drop(person.email)} />
        ))}
        <input
          type="text"
          aria-label="Add people by email"
          placeholder={box.chips.length ? '' : 'Type or paste email addresses'}
          value={box.draft}
          disabled={state.saving}
          onChange={(event) => box.setDraft(event.currentTarget.value)}
          onKeyDown={box.onKeyDown}
          onPaste={box.onPaste}
          onBlur={() => box.commit(box.draft)}
        />
      </div>
      <button
        type="button"
        className="paige-email-sheet-button"
        disabled={state.saving || count === 0}
        onClick={() => void box.invite()}
      >
        <Send size={15} aria-hidden="true" />
        {count > 1 ? `Invite ${count}` : 'Invite'}
      </button>
      {box.notes.map((note) => (
        <p key={note} className="paige-email-sheet-error paige-mobile-note" role="status">
          {note}
        </p>
      ))}
    </div>
  );
}

function Chip({ person, onDrop }: { person: Person; onDrop: () => void }): ReactNode {
  const who = person.name ?? person.email;
  return (
    <span className="paige-mobile-chip">
      {who}
      <button type="button" aria-label={`Take ${who} out`} onClick={onDrop}>
        <X size={12} aria-hidden="true" />
      </button>
    </span>
  );
}

function focusInput(holder: HTMLElement): void {
  holder.querySelector('input')?.focus();
}

/** The chips, the half-typed address, and inviting them; refused people come back as chips. */
function useChips(state: JobMobileState) {
  const [chips, setChips] = useState<Person[]>([]);
  const [draft, setDraft] = useState('');
  const [unusable, setUnusable] = useState<string[]>([]);
  const [told, setTold] = useState<string | null>(null);
  const draftPeople = parsePeople(draft).people;
  const commit = (text: string): Person[] => {
    const { people, unusable: bad } = parsePeople(text);
    const next = merge(chips, people);
    setChips(next);
    setUnusable(bad);
    setDraft(bad.join(', '));
    return next;
  };
  const invite = async () => {
    const people = commit(draft);
    if (people.length === 0) return;
    const outcome = await state.add(people);
    if (!outcome) return;
    const refused = outcome.refused.map((r) => `${r.email} (${refusalWords(r.reason)})`);
    setTold(refused.length > 0 ? `Not added: ${refused.join(', ')}.` : null);
    setChips(people.filter((p) => outcome.refused.some((r) => r.email === p.email)));
  };
  const onKeyDown = keys(draft, chips.length, commit, invite, () => setChips(chips.slice(0, -1)));
  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    commit(`${draft}\n${event.clipboardData.getData('text')}`);
  };
  const drop = (email: string) => setChips(chips.filter((p) => p.email !== email));
  const notes = [
    ...(unusable.length > 0 ? [`Not an address: ${unusable.join(', ')}`] : []),
    ...(told ? [told] : []),
  ];
  return { chips, draft, setDraft, draftPeople, commit, invite, onKeyDown, onPaste, drop, notes };
}

/** Enter, a comma or a semicolon makes the typed address a chip; Enter on an empty box invites; Backspace
 * on an empty box takes the last chip back. */
function keys(
  draft: string,
  chipCount: number,
  commit: (text: string) => void,
  invite: () => Promise<void>,
  dropLast: () => void,
) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    const action = keyAction(event.key, draft, chipCount);
    if (action === 'drop') dropLast();
    else if (action) {
      event.preventDefault();
      if (action === 'invite') void invite();
      else commit(draft);
    }
  };
}

/** What a key does in the box: invite, make a chip, take the last chip back, or nothing. */
function keyAction(
  key: string,
  draft: string,
  chipCount: number,
): 'invite' | 'commit' | 'drop' | null {
  const typed = draft.trim() !== '';
  if (key === 'Enter') return typed ? 'commit' : chipCount > 0 ? 'invite' : null;
  if (key === ',' || key === ';') return typed ? 'commit' : null;
  return key === 'Backspace' && !draft && chipCount > 0 ? 'drop' : null;
}

function merge(chips: Person[], people: Person[]): Person[] {
  const seen = new Set(chips.map((p) => p.email));
  return [...chips, ...people.filter((p) => !seen.has(p.email))];
}

function refusalWords(reason: string): string {
  if (reason === 'seat_limit') return 'the company has no seats left';
  if (reason === 'invite_limit') return 'enough invites for today';
  return 'not an address';
}
